import { useEffect, useRef } from 'react'
import { router, useForm } from '@inertiajs/react'
import {
  buildClientErrors,
  firstErrorFieldId,
  phoneNumberMaxLengthForCode,
  sanitizePhoneNumberInput,
  trimSiteFormData,
  type SiteFormValidationData,
} from '@/entities/organization/sites/siteFormValidation'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { WeeklyScheduleGrid, ALL_DAYS_ON } from '@/shared/ui/weekly-schedule-grid'
import type { WeekdaySchedule } from '@/shared/ui/weekly-schedule-grid'
import { SearchableSelect } from '@/shared/ui/searchable-select'
import { PhoneCodeSelect } from '@/shared/ui/phone-code-select'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { getCsrfToken } from '@/shared/lib/utils'
import { sitesPath, resolveTimezoneSitesPath } from '@/shared/lib/routes'
import type { WebSiteForm } from '@/shared/types/web'

const ADDRESS_RESOLVE_DEBOUNCE_MS = 800

interface TimezoneOption {
  value: number
  label: string
}

interface CountryOption {
  value: number
  label: string
  phoneCode: string
}

type SiteFormData = SiteFormValidationData

interface SiteFormProps {
  timezones: TimezoneOption[]
  countries: CountryOption[]
  initialData?: Partial<WebSiteForm>
  customDaySchedule?: WeekdaySchedule
  /** Always serialized by Sites#new / #edit — required so Follow System never falls back to ALL_DAYS_OFF. */
  parentDaySchedule: WeekdaySchedule
  submitUrl: string
  submitMethod: 'post' | 'patch'
}

const SiteForm = ({
  timezones,
  countries,
  initialData,
  customDaySchedule,
  parentDaySchedule,
  submitUrl,
  submitMethod,
}: SiteFormProps) => {
  const canSubmit = useCan(
    Resources.SITE,
    submitMethod === 'post' ? Actions.CREATE : Actions.UPDATE,
  )
  const form = useForm<SiteFormData>({
    name: initialData?.name ?? '',
    address: initialData?.address ?? '',
    city: initialData?.city ?? '',
    state: initialData?.state ?? '',
    zip: initialData?.zip ?? '',
    phoneNumber: initialData?.phoneNumber ?? '',
    followParentSchedule: initialData?.followParentSchedule ?? true,
    customDaySchedule: customDaySchedule ?? { ...ALL_DAYS_ON },
    timezoneId: initialData?.timezoneId ?? null,
    countryId: initialData?.countryId ?? null,
    phoneCode: initialData?.phoneCode ?? '',
  })

  // Auto-match the timezone to the address. Once the user picks a timezone by hand we stop
  // overriding it (AC3), and we never re-resolve the same address twice. Seeding the ref with
  // the initial address means an unchanged Edit form never re-geocodes on mount.
  const manuallySelectedTimezoneRef = useRef(false)
  const lastResolvedQueryRef = useRef<string | null>(
    JSON.stringify([
      initialData?.address ?? '',
      initialData?.city ?? '',
      initialData?.state ?? '',
      initialData?.zip ?? '',
      initialData?.countryId ?? null,
    ]),
  )

  const { address, city, state, zip, countryId } = form.data
  useEffect(() => {
    if (manuallySelectedTimezoneRef.current) return
    if (!address.trim() && !city.trim() && !zip.trim()) return

    const queryKey = JSON.stringify([address, city, state, zip, countryId])
    if (queryKey === lastResolvedQueryRef.current) return

    const timer = setTimeout(() => {
      lastResolvedQueryRef.current = queryKey
      void (async () => {
        try {
          const response = await fetch(resolveTimezoneSitesPath(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-CSRF-Token': getCsrfToken(),
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              site: { address, city, state, zip, country_id: countryId },
            }),
          })
          if (!response.ok) return

          const data = (await response.json()) as { timezone_id?: number }
          if (data.timezone_id && !manuallySelectedTimezoneRef.current) {
            form.setData('timezoneId', data.timezone_id)
          }
        } catch {
          // Network/geocoding failure: leave the current selection untouched.
        }
      })()
    }, ADDRESS_RESOLVE_DEBOUNCE_MS)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, city, state, zip, countryId])

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    const clientErrors = buildClientErrors(form.data)
    if (Object.keys(clientErrors).length > 0) {
      form.clearErrors()
      form.setError(clientErrors)
      const errorFieldId = firstErrorFieldId(clientErrors)
      if (errorFieldId) {
        document
          .getElementById(errorFieldId)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return
    }

    // Persist trimmed values — buildClientErrors already validates on trimmed copy.
    form.transform((data) => ({ site: trimSiteFormData(data) }))
    if (submitMethod === 'post') {
      form.post(submitUrl)
    } else {
      form.patch(submitUrl)
    }
  }

  const phoneNumberMaxLength = phoneNumberMaxLengthForCode(form.data.phoneCode)

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-xl" noValidate>
      <div className="space-y-1.5">
        <Label htmlFor="name">{t('organization.sites.form.name')} *</Label>
        <Input
          id="name"
          value={form.data.name}
          onChange={(event) => form.setData('name', event.target.value)}
          placeholder={t('organization.sites.form.namePlaceholder')}
          required
        />
        {form.errors.name && <p className="text-sm text-destructive">{form.errors.name}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="address">{t('organization.sites.form.address')} *</Label>
        <Input
          id="address"
          value={form.data.address}
          onChange={(event) => form.setData('address', event.target.value)}
          placeholder={t('organization.sites.form.addressPlaceholder')}
          required
        />
        {form.errors.address && <p className="text-sm text-destructive">{form.errors.address}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="city">{t('organization.sites.form.city')} *</Label>
          <Input
            id="city"
            value={form.data.city}
            onChange={(event) => form.setData('city', event.target.value)}
            placeholder={t('organization.sites.form.cityPlaceholder')}
            required
          />
          {form.errors.city && <p className="text-sm text-destructive">{form.errors.city}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="state">{t('organization.sites.form.state')} *</Label>
          <Input
            id="state"
            value={form.data.state}
            onChange={(event) => form.setData('state', event.target.value)}
            placeholder={t('organization.sites.form.statePlaceholder')}
            required
          />
          {form.errors.state && <p className="text-sm text-destructive">{form.errors.state}</p>}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="zip">{t('organization.sites.form.zip')} *</Label>
        <Input
          id="zip"
          value={form.data.zip}
          onChange={(event) => form.setData('zip', event.target.value)}
          placeholder={t('organization.sites.form.zipPlaceholder')}
          required
        />
        {form.errors.zip && <p className="text-sm text-destructive">{form.errors.zip}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="phoneCode">{t('organization.sites.form.phoneCode')}</Label>
          <PhoneCodeSelect
            id="phoneCode"
            value={form.data.phoneCode}
            onValueChange={(value) => {
              form.setData({
                ...form.data,
                phoneCode: value,
                phoneNumber: sanitizePhoneNumberInput(value, form.data.phoneNumber),
              })
            }}
          />
          {form.errors.phoneCode && (
            <p className="text-sm text-destructive">{form.errors.phoneCode}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="phoneNumber">{t('organization.sites.form.phoneNumber')}</Label>
          <Input
            id="phoneNumber"
            type="tel"
            value={form.data.phoneNumber}
            maxLength={phoneNumberMaxLength}
            onChange={(event) =>
              form.setData(
                'phoneNumber',
                sanitizePhoneNumberInput(form.data.phoneCode, event.target.value),
              )
            }
            placeholder={t('organization.sites.form.phoneNumberPlaceholder')}
          />
          {form.errors.phoneNumber && (
            <p className="text-sm text-destructive">{form.errors.phoneNumber}</p>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="timezoneId">{t('organization.sites.form.timezone')} *</Label>
        <SearchableSelect
          id="timezoneId"
          value={form.data.timezoneId?.toString() ?? ''}
          aria-required
          onValueChange={(value) => {
            manuallySelectedTimezoneRef.current = true
            form.setData('timezoneId', value ? Number(value) : null)
          }}
          placeholder={t('organization.sites.form.timezone')}
          options={timezones.map((timezone) => ({
            value: timezone.value.toString(),
            label: timezone.label,
          }))}
        />
        {form.errors.timezoneId && (
          <p className="text-sm text-destructive">{form.errors.timezoneId}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="countryId">{t('organization.sites.form.country')} *</Label>
        <SearchableSelect
          id="countryId"
          value={form.data.countryId?.toString() ?? ''}
          aria-required
          onValueChange={(value) => form.setData('countryId', value ? Number(value) : null)}
          placeholder={t('organization.sites.form.country')}
          options={countries.map((country) => ({
            value: country.value.toString(),
            label: country.label,
          }))}
        />
        {form.errors.countryId && (
          <p className="text-sm text-destructive">{form.errors.countryId}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label>{t('schedule.loggingSchedule')}</Label>
        <WeeklyScheduleGrid
          value={form.data.customDaySchedule}
          onChange={(next) => form.setData('customDaySchedule', next)}
          followParent={form.data.followParentSchedule}
          onFollowParentChange={(checked) => form.setData('followParentSchedule', checked)}
          parentPreview={parentDaySchedule}
          parentLabel={t('schedule.followSystemSchedule')}
        />
        {form.errors.customDaySchedule && (
          <p className="text-sm text-destructive">{form.errors.customDaySchedule}</p>
        )}
      </div>

      {!canSubmit && <NoAccessNotice />}

      <div className="flex gap-3 pt-2">
        {canSubmit && (
          <Button type="submit" disabled={form.processing}>
            {form.processing
              ? t('organization.sites.form.submitting')
              : t('organization.sites.form.submit')}
          </Button>
        )}
        <Button type="button" variant="outline" onClick={() => router.visit(sitesPath())}>
          {t('organization.sites.form.cancel')}
        </Button>
      </div>
    </form>
  )
}

export default SiteForm
